import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@colorglo/ui/components";
import { blockerIcons, iconSize } from "@colorglo/ui/icons";
import { useId, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { type StaffMetrics, useStaffMetrics } from "../../api/settings.ts";
import { useLocale, useT } from "../../lib/locale.ts";
import { shortDate } from "../../lib/network.ts";
import { formatDayHeading, formatMoney, formatTime, today, zoned } from "../../lib/time.ts";
import { SettingsCard, SettingsError, SettingsLoading, SettingsSection } from "./shell.tsx";

const PERIODS = ["30", "90", "365"] as const;
type Period = (typeof PERIODS)[number];
type Kpis = StaffMetrics["kpis"];

export function StaffOverview({
  franchiseId,
  resourceId,
  name,
  timezone,
}: {
  readonly franchiseId: string;
  readonly resourceId: string;
  readonly name: string;
  readonly timezone: string;
}) {
  const t = useT();
  const id = useId();
  const [period, setPeriod] = useState<Period>("30");
  const to = today(timezone);
  const from = to.subtract({ days: Number(period) - 1 });
  const metrics = useStaffMetrics(franchiseId, resourceId, from.toString(), to.toString());

  return (
    <SettingsSection
      heading={t("settings.staff.overview.heading")}
      body={t("settings.staff.overview.body", { name })}
    >
      <div className="flex items-center gap-3">
        <label htmlFor={`${id}-period`} className="text-body-sm text-muted-foreground">
          {t("settings.staff.overview.period")}
        </label>
        <Select value={period} onValueChange={(next) => setPeriod(next as Period)}>
          <SelectTrigger id={`${id}-period`} className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((entry) => (
              <SelectItem key={entry} value={entry}>
                {t(`settings.staff.overview.period.${entry}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {metrics.isPending ? (
        <SettingsLoading />
      ) : metrics.isError ? (
        <SettingsError onRetry={() => void metrics.refetch()} />
      ) : (
        <OverviewBody metrics={metrics.data} timezone={timezone} />
      )}
    </SettingsSection>
  );
}

function OverviewBody({
  metrics,
  timezone,
}: {
  readonly metrics: StaffMetrics;
  readonly timezone: string;
}) {
  const t = useT();
  const locale = useLocale();
  const percent = (value: number | null) =>
    value === null
      ? "—"
      : new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(value);
  const money = (value: number | null) =>
    value === null ? "—" : formatMoney(value, metrics.currency, locale);
  const hours = (minutes: number) =>
    t("settings.staff.overview.hours", {
      hours: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(minutes / 60),
    });
  const count = (value: number) => new Intl.NumberFormat(locale).format(value);

  const tiles: {
    readonly key: string;
    readonly label: string;
    readonly format: (kpis: Kpis) => string;
    readonly hint?: string;
  }[] = [
    {
      key: "jobs",
      label: t("settings.staff.overview.kpi.jobs_completed"),
      format: (kpis) => count(kpis.jobs_completed),
    },
    {
      key: "worked",
      label: t("settings.staff.overview.kpi.worked"),
      format: (kpis) => hours(kpis.worked_minutes),
    },
    {
      key: "revenue",
      label: t("settings.staff.overview.kpi.revenue"),
      format: (kpis) => money(kpis.revenue_minor),
    },
    {
      key: "job_value",
      label: t("settings.staff.overview.kpi.job_value"),
      format: (kpis) =>
        money(kpis.job_value_minor === null ? null : Math.round(kpis.job_value_minor / 100) * 100),
    },
    {
      key: "utilisation",
      label: t("settings.staff.overview.kpi.utilisation"),
      format: (kpis) => percent(kpis.utilisation),
      hint: t("settings.staff.overview.hint.utilisation"),
    },
    {
      key: "no_show",
      label: t("settings.staff.overview.kpi.no_show_rate"),
      format: (kpis) => percent(kpis.no_show_rate),
    },
    {
      key: "cancellation",
      label: t("settings.staff.overview.kpi.cancellation_rate"),
      format: (kpis) => percent(kpis.cancellation_rate),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        {tiles.map((tile) => (
          <Card key={tile.key}>
            <CardHeader>
              <CardDescription>{tile.label}</CardDescription>
              <CardTitle className="text-h1 break-words" data-numeric="">
                {tile.format(metrics.kpis)}
              </CardTitle>
              <p className="text-caption text-muted-foreground">
                {t("settings.staff.overview.previous", { value: tile.format(metrics.previous) })}
              </p>
              {tile.hint !== undefined && (
                <p className="text-caption text-muted-foreground">{tile.hint}</p>
              )}
            </CardHeader>
          </Card>
        ))}
      </div>

      <SettingsCard>
        <h3 className="text-h3">{t("settings.staff.overview.time_off")}</h3>
        <div className="flex flex-wrap gap-8">
          {(["vacation", "sick", "training"] as const).map((kind) => {
            const Icon = blockerIcons[kind];
            return (
              <div key={kind} className="flex items-center gap-3">
                <Icon {...iconSize.control} aria-hidden="true" className="text-muted-foreground" />
                <div className="flex flex-col">
                  <span className="text-body-sm text-muted-foreground">
                    {t(`calendar.blocker.${kind}`)}
                  </span>
                  <span className="text-h2" data-numeric="">
                    {t("settings.staff.overview.days", {
                      days: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
                        metrics.time_off[kind],
                      ),
                    })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </SettingsCard>

      <Charts metrics={metrics} percent={percent} />

      <div className="grid gap-6 lg:grid-cols-2">
        <SettingsCard>
          <h3 className="text-h3">{t("settings.staff.overview.services")}</h3>
          {metrics.services.length === 0 ? (
            <p className="text-body-sm text-muted-foreground">
              {t("settings.staff.overview.empty")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("settings.staff.overview.services.service")}</TableHead>
                  <TableHead className="text-right">
                    {t("settings.staff.overview.services.jobs")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.services.map((service) => (
                  <TableRow key={service.service_id}>
                    <TableCell>{service.name[locale] ?? service.name.sv}</TableCell>
                    <TableCell className="text-right" data-numeric="">
                      {count(service.jobs)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SettingsCard>

        <SettingsCard>
          <h3 className="text-h3">{t("settings.staff.overview.recent")}</h3>
          {metrics.recent_jobs.length === 0 ? (
            <p className="text-body-sm text-muted-foreground">
              {t("settings.staff.overview.empty")}
            </p>
          ) : (
            <ul className="flex flex-col">
              {metrics.recent_jobs.map((job) => {
                const when = zoned(job.starts_at, timezone);
                return (
                  <li
                    key={job.id}
                    className="flex flex-wrap items-baseline gap-x-3 border-b border-border py-2 last:border-0"
                  >
                    <span className="text-body-sm tabular-nums">
                      {formatDayHeading(when.toPlainDate(), locale)} {formatTime(when)}
                    </span>
                    <span className="text-body">
                      {job.services.map((name) => name[locale] ?? name.sv).join(", ")}
                    </span>
                    <span className="ml-auto text-body-sm text-muted-foreground">
                      {t(`calendar.status.${job.status}` as "calendar.status.completed")}
                      {job.status === "completed" && ` · ${money(job.total_minor)}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </SettingsCard>
      </div>
    </div>
  );
}

const AXIS = {
  stroke: "var(--color-chart-grid)",
  tick: { fill: "var(--color-muted-foreground)", fontSize: 11 },
  tickLine: false,
} as const;

const TOOLTIP = {
  contentStyle: {
    background: "var(--color-popover)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    fontSize: 12,
  },
  labelStyle: { color: "var(--color-muted-foreground)" },
  itemStyle: { color: "var(--color-foreground)" },
} as const;

function Charts({
  metrics,
  percent,
}: {
  readonly metrics: StaffMetrics;
  readonly percent: (value: number | null) => string;
}) {
  const t = useT();
  const locale = useLocale();
  const tableId = useId();
  const [showTable, setShowTable] = useState(false);
  const points = metrics.series.map((point) => ({ ...point }));
  const label = (value: unknown) => shortDate(String(value), locale);

  return (
    <SettingsCard>
      <div className="grid gap-6 lg:grid-cols-2">
        <figure className="flex flex-col gap-2">
          <figcaption className="text-body-sm text-muted-foreground">
            {t("settings.staff.overview.chart.jobs")}
          </figcaption>
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="var(--color-chart-grid)" vertical={false} />
                <XAxis dataKey="start" tickFormatter={label} minTickGap={24} {...AXIS} />
                <YAxis allowDecimals={false} axisLine={false} width={32} {...AXIS} />
                <Tooltip
                  cursor={{ fill: "var(--color-chart-grid)", opacity: 0.4 }}
                  labelFormatter={label}
                  formatter={(value) => [String(value), t("settings.staff.overview.chart.jobs")]}
                  {...TOOLTIP}
                />
                <Bar
                  dataKey="completed"
                  fill="var(--color-chart-1)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={24}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </figure>

        <figure className="flex flex-col gap-2">
          <figcaption className="text-body-sm text-muted-foreground">
            {t("settings.staff.overview.chart.utilisation")}
          </figcaption>
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="var(--color-chart-grid)" vertical={false} />
                <XAxis dataKey="start" tickFormatter={label} minTickGap={24} {...AXIS} />
                <YAxis
                  axisLine={false}
                  width={44}
                  domain={[0, "auto"]}
                  tickFormatter={(value: number) => percent(value)}
                  {...AXIS}
                />
                <Tooltip
                  cursor={{ stroke: "var(--color-chart-grid)", strokeWidth: 1 }}
                  labelFormatter={label}
                  formatter={(value) => [
                    percent(typeof value === "number" ? value : null),
                    t("settings.staff.overview.chart.utilisation"),
                  ]}
                  {...TOOLTIP}
                />
                <Line
                  type="linear"
                  dataKey="utilisation"
                  stroke="var(--color-chart-1)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--color-card)" }}
                  isAnimationActive={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </figure>
      </div>

      <button
        type="button"
        className="self-start text-caption text-muted-foreground underline underline-offset-2 hover:text-foreground"
        aria-expanded={showTable}
        aria-controls={tableId}
        onClick={() => setShowTable((open) => !open)}
      >
        {t("settings.staff.overview.chart.table")}
      </button>

      {showTable && (
        <div id={tableId} className="max-h-64 overflow-y-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("settings.staff.overview.chart.period")}</TableHead>
                <TableHead className="text-right">
                  {t("settings.staff.overview.chart.jobs")}
                </TableHead>
                <TableHead className="text-right">
                  {t("settings.staff.overview.chart.utilisation")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {points.map((point) => (
                <TableRow key={point.start}>
                  <TableCell>{point.start}</TableCell>
                  <TableCell className="text-right" data-numeric="">
                    {point.completed}
                  </TableCell>
                  <TableCell className="text-right" data-numeric="">
                    {percent(point.utilisation)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </SettingsCard>
  );
}
